import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { cookieUtils } from '../API/ApiService';
import { useAuth } from '../Auth/useAuth';
import toast from 'react-hot-toast';
import LoaderOverlay from '../Loader/LoaderOverlay';

const AppKeys = () => {
    const { validateSdkToken, checkToken } = useAuth();
    const formDataRef = useRef(null);
    const [state, setState] = useState({
        formData: {
            consumerID: '',
            passPhrase: '',
            appSpecificToken: ''
        },
        isLoading: false,
        isDisabled: false,
        message: { type: '', text: '' }
    });

    useEffect(() => {
          checkToken();
    }, [checkToken]);

    useEffect(() => {
        const savedKeys = cookieUtils.get('savedAppKeys');
        if (savedKeys) {
            try {
                const decodedKeys = JSON.parse(atob(savedKeys));
                setState(prev => ({
                    ...prev,
                    formData: decodedKeys,
                    isDisabled: true
                }));
            } catch (error) {
                console.error('Error loading saved keys:', error);
            }
        }
    }, []);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                [name]: value
            }
        }));
    }, []);

    formDataRef.current = state.formData;

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setState(prev => ({ ...prev, isLoading: true, message: { type: '', text: '' } }));

        try {
            const currentFormData = formDataRef.current;
            const data = await api.validateAppKeys(currentFormData);
            cookieUtils.set('appSpecificToken', currentFormData.appSpecificToken, {
                expires: new Date(data.data.expiresAt)
            });

            if (data.status) {
                const encodedKeys = btoa(JSON.stringify(currentFormData));
                cookieUtils.set('savedAppKeys', encodedKeys, {
                    expires: new Date(data.data.expiresAt)
                });
                await validateSdkToken();
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                    isDisabled: true,
                    message: { type: 'success', text: 'App keys saved successfully!' }
                }));
            } else {
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                    message: { 
                        type: 'error', 
                        text: 'Failed to save app keys' 
                    }
                }));
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                isLoading: false,
                message: { 
                    type: 'error', 
                    text: 'Failed to save app keys' 
                }
            }));
            toast.error('An error occurred while saving app keys');
        }
    }, [validateSdkToken]);

    const handleClear = useCallback(() => {
        setState(prev => ({
            ...prev,
            isDisabled: prev.isDisabled ? false : prev.isDisabled,
            formData: prev.isDisabled ? prev.formData : {
                consumerID: '',
                passPhrase: '',
                appSpecificToken: ''
            },
            message: { type: '', text: '' }
        }));
    }, []);

    const maskValue = (value, type) => {
        if (!value) return '';
        switch (type) {
            case 'consumerID':
                return `${value.slice(0, 4)}${'*'.repeat(value.length - 4)}`;
            case 'passPhrase':
                return '•'.repeat(value.length);
            case 'appSpecificToken':
                return `${value.slice(0, 4)}${'*'.repeat(value.length - 4)}`;
            default:
                return value;
        }
    };

    return state.isLoading ? (
        <LoaderOverlay />
    ) : (
        <div>
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">App Keys</h2>
                <p className="text-gray-600">Manage your application API keys</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="max-w-2xl">
                    <h3 className="text-lg font-medium text-gray-800 mb-6">Configure API Keys</h3>
                    
                    {state.message.text && (
                        <div className={`mb-6 p-4 rounded-md ${
                            state.message.type === 'success' 
                                ? 'bg-green-50 border border-green-200 text-green-800' 
                                : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                            {state.message.text}
                        </div>
                    )}

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Consumer ID
                            </label>
                            <input
                                type="text"
                                name="consumerID"
                                value={state.isDisabled ? maskValue(state.formData.consumerID, 'consumerID') : state.formData.consumerID}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your Consumer ID"
                                disabled={state.isLoading || state.isDisabled}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Passphrase
                            </label>
                            <input
                                type="password"
                                name="passPhrase"
                                value={state.isDisabled ? maskValue(state.formData.passPhrase, 'passPhrase') : state.formData.passPhrase}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your passphrase"
                                disabled={state.isLoading || state.isDisabled}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                App Specific Token
                            </label>
                            <textarea
                                name="appSpecificToken"
                                value={state.isDisabled ? maskValue(state.formData.appSpecificToken, 'appSpecificToken') : state.formData.appSpecificToken}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                placeholder="Enter your app specific token"
                                disabled={state.isLoading || state.isDisabled}
                            />
                        </div>

                        <div className="flex space-x-4">
                            <button
                                onClick={handleSubmit}
                                disabled={state.isLoading || !state.formData.consumerID || !state.formData.passPhrase || !state.formData.appSpecificToken}
                                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {state.isLoading ? 'Saving...' : 'Save Keys'}
                            </button>
                            
                            <button
                                onClick={handleClear}
                                disabled={state.isLoading}
                                className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {state.isDisabled ? 'Enter Again' : 'Clear'}
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">Information</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Consumer ID: Your unique identifier for API access</li>
                            <li>• Passphrase: Secure passphrase for authentication</li>
                            <li>• App Specific Token: Token specific to your application</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppKeys;